import * as React from 'react';
import { Link } from 'react-router-dom';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import { Input } from 'reactstrap';

import { devInfoStore } from '@app/Stores/DevInfoStore';
import { navigationStore } from '@app/Stores/NavigationStore';
import { appStore } from '@app/AppStore';
import { configurationStore } from '@app/Stores/ConfigurationStore';

import { Configurations } from '@app/AppConstants/Configurations';
import { applyDecorators, EmptyProps } from "@app/Helpers/Decorator";
import { getEncodedToken } from '@app/Helpers/JWTHelper';
import DateTimeService from '@app/Services/DateTimeService';
import { MatchComparer, stringSearchService } from '@app/Services/StringSearchService';

import { AzureAuth, UserPermissions } from './AzureAuth';
import { LoginSection } from '@app/Components/LoginSection';
import { HelpLink } from '@app/Components/HelpLink';
import { modalService } from '@app/Components/Modal';
import { HoverContainer } from '@app/Components/HoverContainer';
import ThemeSwitcher from './ThemeSwitcher';

const CreateStandByReportEmailDialog = React.lazy(() => import('@app/Components/Dialogs/CreateStandByReportEmailDialog'));
const QualityLoopExportDialog = React.lazy(() => import('@app/Components/Dialogs/QualityLoopExportDialog'));

type ClickHandler = () => void;
type GetStateHandler = () => boolean;

export enum MenuSection {
    View,
    Tools
}

export type MenuLinkItem = {
    type: 'link';
    title: string;
    toUrl: string | { pathname: string };
    openInNewTab?: boolean;
    keywords?: string[];
};

export type MenuActionItem = {
    type: 'action';
    title: string;
    onClick: () => void;
    className?: string;
};

export type MenuSeparatorItem = {
    type: 'separator';
    title: string;
    id: string;
};

export type MenuItem = MenuLinkItem | MenuActionItem | MenuSeparatorItem;

export type DynamicMenuItem = {
    id: string;
    title: string;
    section: MenuSection;
    onClick?: ClickHandler;
    onGetState?: GetStateHandler;
    order: number;
    isHidden?: boolean | (() => boolean);
};

export type ExtensionItem = {
    id: string;
    itemRender: () => JSX.Element;
    className?: string;
    isHidden?: boolean;
};

@observer
export class NavMenu extends React.Component {
    @observable private _helpLink: string = '';
    @observable private _enableExperienceReport: boolean = false;
    @observable private _enableEfaUi: boolean = false;
    @observable private _isAnyMenuItemVisible: boolean = false;
    @observable private _featureEnableCabinAttestation: boolean = false;
    @observable private _featureEnableSimCourseOverview: boolean;
    @observable private _enableReserveTool: boolean = false;
    @observable private _hasReachGavCockpit2024JulyEffectiveDate: boolean = false;
    @observable private _searchValue: string = '';

    constructor(props: EmptyProps) {
        super(props);
        applyDecorators(this);
    }

    componentDidMount() {
        setTimeout(() => {
            const script = document.createElement('script');
            script.type = 'text/javascript';
            script.src = '/js/ads.js';
            script.onerror = () => {
                navMenuStore.hasRenderBlocker = true;
            };
            script.onload = () => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                if (!(window as any).canRunAds) {
                    navMenuStore.hasRenderBlocker = true;
                }
            };
            document.head && document.head.appendChild(script);

            const adsDiv = document.createElement('div') as HTMLDivElement;
            adsDiv.className = 'ad ads adsbox doubleclick ad-placement ad-placeholder adbadge BannerAd';
            adsDiv.style.position = 'fixed;';
            adsDiv.style.bottom = '0';
            adsDiv.style.left = '0';
            adsDiv.style.width = '100%';
            adsDiv.style.height = '1px;';
            document.body.appendChild(adsDiv);

            setTimeout(() => {
                if (window.getComputedStyle(adsDiv).getPropertyValue('display') == 'none') {
                    navMenuStore.hasRenderBlocker = true;
                }
                adsDiv.remove();
            }, 1000);

        }, 2000);

        void configurationStore.get(Configurations.globalGroup, Configurations.helpLinkKey).then((response) => {
            this._helpLink = response || '';
        });
        void configurationStore.get(Configurations.featureGroup, Configurations.featureEnableExperienceReport).then((response) => {
            this._enableExperienceReport = response?.toLowerCase() === 'true';
        });
        void configurationStore.get(Configurations.featureGroup, Configurations.featureEnableCabinAttestation).then((response) => {
            this._featureEnableCabinAttestation = response?.toLowerCase() === 'true';
        });
        void configurationStore.get(Configurations.featureGroup, Configurations.featureEnableSimCourseOverview).then((response) => {
            this._featureEnableSimCourseOverview = response?.toLowerCase() === 'true';
        });
        void configurationStore.get(Configurations.featureGroup, Configurations.featureEnableEfaUi).then((response) => {
            this._enableEfaUi = response?.toLowerCase() === 'true';
        });
        void configurationStore.get(Configurations.featureGroup, Configurations.featureEnableReserveTool).then((response) => {
            this._enableReserveTool = response?.toLowerCase() === 'true';
        });
        void configurationStore.get(Configurations.validationGroup, Configurations.gavCockpit2024JulyEffectiveDate).then((response) => {
            const effectiveDate = response ? new Date(response) : null;
            const today = DateTimeService.today();
            this._hasReachGavCockpit2024JulyEffectiveDate = !!effectiveDate && effectiveDate <= today;
        });
    }

    @computed
    get _tools() {
        const isCrewPortalConfigured = !!(window as { crewPortalUrl?: string }).crewPortalUrl;
        const hasAccessToCockpit = AzureAuth.hasAccess(UserPermissions.RosterCockpitRead);
        const hasAccessToCabin = AzureAuth.hasAccess(UserPermissions.RosterCabinRead);
        const hasAccessToVacManagement = AzureAuth.hasAccess(UserPermissions.VacManagementRead);
        const hasAccessToReserveManagement = AzureAuth.hasAccess(UserPermissions.ReserveManagementRead);
        const hasAccessToJCR = AzureAuth.hasAccess(UserPermissions.JCRImportRead);
        const hasAccessToLicenseManagement = AzureAuth.hasAccess(UserPermissions.LicenseManagementRead);
        const hasAccessToRotationTool = AzureAuth.hasAccess(UserPermissions.RotationToolRead);
        const hasAccessToAeropers = AzureAuth.hasAccess(UserPermissions.ToolAeroPersRead);
        const hasAccessToEfa = this._enableEfaUi && AzureAuth.hasAccess(UserPermissions.ToolEfaRead);
        const hasAccessToHotelTools = AzureAuth.hasAccess(UserPermissions.ToolHotelManagement);
        const hasAccessToCapacityTool = AzureAuth.hasAccess(UserPermissions.RequestCapacityTool);
        const hasAccessToBlockDaysManagement = AzureAuth.hasAccess(UserPermissions.BlockingDaysFlightsRead);
        const hasAccessToCrewApiGenDecConfig = AzureAuth.hasAccess([UserPermissions.CrewApiRead, UserPermissions.GenDecRead]);
        const hasAccessToCustomPersonManagement = AzureAuth.hasAccess([UserPermissions.MarshalsInfoRead, UserPermissions.FseInfoRead]);
        const hasAccessToTemplatesManagement = AzureAuth.hasAccess(UserPermissions.TemplateManagementRead);
        const hasAccessToAdminTools = AzureAuth.hasAccess(UserPermissions.AdminToolsRead);
        const hasAccessToRequestTool = AzureAuth.hasCrewPortalAccess();
        const hasAccessToHomeStudy = AzureAuth.hasAccess([
            UserPermissions.RosterCabinWritePlanning,
            UserPermissions.RosterCabinWriteTracking,
            UserPermissions.RosterCockpitWritePlanning,
            UserPermissions.RosterCockpitWriteTracking,
            UserPermissions.RosterGroundWritePlanning,
            UserPermissions.RosterGroundWriteTracking
        ]);
        const hasAccessToEmailViewer = AzureAuth.hasAccess(UserPermissions.EmailsRead);
        const hasAccessToCourseManagement = AzureAuth.hasAccess(UserPermissions.CourseManagementRead);
        const hasAccessToStandbyReportEmailCreation = hasAccessToCockpit && hasAccessToCabin;
        const hasAccessToQualityExport = AzureAuth.hasAccess(UserPermissions.QualityLoopExportReadPlan);

        const tools: MenuItem[] = [];

        hasAccessToRequestTool && tools.push({ type: 'link', toUrl: { pathname: appStore.crewPortalUrl }, title: 'Crew Portal', openInNewTab: !!isCrewPortalConfigured });
        hasAccessToVacManagement && tools.push({ type: 'link', toUrl: '/vacation-request-management', title: 'Vacation Request Management', openInNewTab: navigationStore.isHomePage });
        this._enableReserveTool && hasAccessToReserveManagement && tools.push({ type: 'link', toUrl: '/reserve-request-management', title: 'Reserve Request Management', openInNewTab: navigationStore.isHomePage });
        hasAccessToHotelTools && tools.push({ type: 'link', toUrl: '/hotel-management-monthly', title: 'Hotel Management Monthly' });
        hasAccessToHotelTools && tools.push({ type: 'link', toUrl: '/hotel-management-daily', title: 'Hotel Management Daily' });
        hasAccessToHomeStudy && tools.push({ type: 'link', toUrl: '/home-study', title: 'Home Study', openInNewTab: navigationStore.isHomePage });
        hasAccessToJCR && tools.push({ type: 'link', toUrl: '/jeppesen-tool', title: 'Jeppesen Tool' });
        hasAccessToAeropers && tools.push({ type: 'link', toUrl: '/aeropers-interface', title: 'AeroPers Interface' });
        hasAccessToEfa && tools.push({ type: 'link', toUrl: '/efa-interface', title: 'Efa Interface' });
        hasAccessToRotationTool && tools.push({ type: 'link', toUrl: '/rotation-tool', title: 'Rotation Tool' });
        hasAccessToLicenseManagement && tools.push({ type: 'link', toUrl: '/license-management', title: 'License Management' });
        hasAccessToCustomPersonManagement && tools.push({ type: 'link', toUrl: '/custom-person-management', title: [AzureAuth.hasAccess(UserPermissions.MarshalsInfoRead) ? 'Marshals' : '', AzureAuth.hasAccess(UserPermissions.FseInfoRead) ? 'FSE' : ''].filter(x => !!x).join('/') + ' Management' });
        hasAccessToCapacityTool && tools.push({ type: 'link', toUrl: '/request-capacity-tool', title: 'Request Capacity Tool' });
        hasAccessToBlockDaysManagement && tools.push({ type: 'link', toUrl: '/request-tool-configuration', title: 'Request Tool Configuration' });
        hasAccessToCrewApiGenDecConfig && tools.push({ type: 'link', toUrl: '/crewapi-gendec-configuration', title: [AzureAuth.hasAccess([UserPermissions.CrewApiRead]) ? 'Crew API' : '', AzureAuth.hasAccess([UserPermissions.GenDecRead]) ? 'GenDec' : ''].filter(x => !!x).join('/') + ' Configuration' });
        this._featureEnableCabinAttestation && hasAccessToCourseManagement && tools.push({ type: 'link', toUrl: '/course-management', title: 'Course Management', openInNewTab: navigationStore.isHomePage });
        hasAccessToStandbyReportEmailCreation && tools.push({ type: 'action', title: 'Create Crewlage Report Email', onClick: () => void modalService.show(CreateStandByReportEmailDialog) });
        hasAccessToQualityExport && tools.push({ type: 'action', title: 'Quality Loop Export', onClick: () => void modalService.show(QualityLoopExportDialog) });

        const adminTools: MenuItem[] = [];
        hasAccessToAdminTools && adminTools.push({ type: 'link', toUrl: '/health-board', title: 'Health Board' });
        hasAccessToAdminTools && adminTools.push({ type: 'link', toUrl: '/job-list', title: 'Job List' });
        hasAccessToAdminTools && adminTools.push({ type: 'link', toUrl: '/maintenance', title: 'Maintenance' });
        hasAccessToAdminTools && adminTools.push({ type: 'link', toUrl: '/permissions-tool', title: 'Permissions Tool' });
        hasAccessToEmailViewer && adminTools.push({ type: 'link', toUrl: '/email-viewer', title: 'Email Viewer' });
        hasAccessToTemplatesManagement && adminTools.push({ type: 'link', toUrl: '/templates-management', title: 'Templates Management' });
        hasAccessToAdminTools && adminTools.push({ type: 'link', toUrl: '/configs', title: 'Configs' });

        if (adminTools.length > 0) {
            tools.push({ type: 'separator', title: '-', id: 'adminToolSeparator' });
            tools.push(...adminTools);
        }

        const dynamicItems = this._getDynamicItems(MenuSection.Tools);
        tools.unshift(...dynamicItems);

        return tools;
    }

    @computed
    private get _reports() {
        const hasAccessToCockpit = AzureAuth.hasAccess(UserPermissions.RosterCockpitRead);
        const hasAccessToCabin = AzureAuth.hasAccess(UserPermissions.RosterCabinRead);
        const hasAccessToDutyRelatedReport = AzureAuth.hasAccess(UserPermissions.DutyRelatedReportsRead);
        const hasAccessToGroundStaffStatisticReport = AzureAuth.hasAccess(UserPermissions.RosterGroundRead);
        const hasAccessToPersonReport = hasAccessToDutyRelatedReport && AzureAuth.hasAccess([
            UserPermissions.ReadPersonDetails,
            UserPermissions.ReadPersonDetailsCockpit,
            UserPermissions.ReadPersonDetailsCabin,
            UserPermissions.ReadPersonDetailsGround
        ]);
        const hasAccessToSeniorityReport = AzureAuth.hasAccess(UserPermissions.SeniorityReportRead);
        const hasAccessToCateringReport = AzureAuth.hasAccess(UserPermissions.CateringReportRead);
        const hasAccessToAreaRouteCompetenceReport = AzureAuth.hasAccess(UserPermissions.RosterCockpitRead);
        const hasAccessToOutageReport = AzureAuth.hasAccess(UserPermissions.RosterCockpitWritePlanning)
            && AzureAuth.hasAccess(UserPermissions.RosterCabinWritePlanning)
            && AzureAuth.hasAccess(UserPermissions.RosterGroundWritePlanning)
            && AzureAuth.hasAccess(UserPermissions.RosterFseRead)
            && AzureAuth.hasAccess(UserPermissions.RosterMarshalRead);
        const hasAccessToCaCoReport = hasAccessToCockpit || hasAccessToCabin;
        const hasAccessToDosimetryReport = (hasAccessToCockpit || hasAccessToCabin) && hasAccessToDutyRelatedReport;
        const hasAccessToCourseManagement = AzureAuth.hasAccess(UserPermissions.CourseManagementRead);

        const reports: MenuItem[] = [];

        hasAccessToPersonReport && reports.push({ type: 'link', toUrl: '/reports/person', title: 'Person', openInNewTab: navigationStore.isHomePage });
        hasAccessToDutyRelatedReport && reports.push({ type: 'link', toUrl: '/reports/recurrent-training', title: 'Recurrent Training', openInNewTab: navigationStore.isHomePage });
        hasAccessToCaCoReport && reports.push({ type: 'link', toUrl: '/reports/standby', title: 'Standby', openInNewTab: navigationStore.isHomePage });
        hasAccessToDutyRelatedReport && reports.push({ type: 'link', toUrl: '/reports/planned-trainings', title: 'Trainings Report', openInNewTab: navigationStore.isHomePage });
        hasAccessToCockpit && reports.push({ type: 'link', toUrl: '/reports/airport-recency', title: 'Airport Recency Report', openInNewTab: navigationStore.isHomePage });
        hasAccessToAreaRouteCompetenceReport && reports.push({ type: 'link', toUrl: '/reports/area-route-competency-recency', title: 'Area Route Competency Report', openInNewTab: navigationStore.isHomePage });
        hasAccessToDutyRelatedReport && reports.push({ type: 'link', toUrl: '/reports/sectors', title: 'Sectors', openInNewTab: navigationStore.isHomePage });
        hasAccessToDutyRelatedReport && reports.push({ type: 'link', toUrl: '/reports/crew-duty-list', title: 'Crew Duty List', openInNewTab: navigationStore.isHomePage });
        hasAccessToDutyRelatedReport && reports.push({ type: 'link', toUrl: '/reports/roster', title: 'Roster Report', openInNewTab: navigationStore.isHomePage });
        hasAccessToDutyRelatedReport && reports.push({ type: 'link', toUrl: '/reports/comment', title: 'Comments Report', openInNewTab: navigationStore.isHomePage });
        hasAccessToDutyRelatedReport && reports.push({ type: 'link', toUrl: '/reports/off-days', title: 'OFF Days Report', openInNewTab: navigationStore.isHomePage });
        hasAccessToCockpit && reports.push({ type: 'link', toUrl: '/reports/aircraft-type-recency-cockpit', title: 'Aircraft Type Recency - Cockpit', openInNewTab: navigationStore.isHomePage });
        hasAccessToCabin && reports.push({ type: 'link', toUrl: '/reports/aircraft-type-recency-cabin', title: 'Aircraft Type Recency - Cabin', openInNewTab: navigationStore.isHomePage });
        hasAccessToCaCoReport && reports.push({ type: 'link', toUrl: '/reports/doox', title: 'DOOX Report', openInNewTab: navigationStore.isHomePage });
        hasAccessToDutyRelatedReport && reports.push({ type: 'link', toUrl: '/reports/experience', title: 'Experience Report', openInNewTab: navigationStore.isHomePage });
        hasAccessToDutyRelatedReport && reports.push({ type: 'link', toUrl: '/reports/simulator-license-status', title: 'Simulator/License Status', openInNewTab: navigationStore.isHomePage });
        hasAccessToDutyRelatedReport && reports.push({ type: 'link', toUrl: '/reports/errors', title: 'Errors Report', openInNewTab: navigationStore.isHomePage });
        hasAccessToDutyRelatedReport && reports.push({ type: 'link', toUrl: '/reports/person-vy-details', title: 'Person VY Details', openInNewTab: navigationStore.isHomePage });
        hasAccessToDutyRelatedReport && reports.push({ type: 'link', toUrl: '/reports/tzv', title: 'TZV Year Overview', openInNewTab: navigationStore.isHomePage });
        hasAccessToDutyRelatedReport && reports.push({ type: 'link', toUrl: '/reports/xstar-monitoring', title: 'X* Monitoring Report', openInNewTab: navigationStore.isHomePage });
        hasAccessToCateringReport && reports.push({ type: 'link', toUrl: '/reports/catering', title: 'Catering Report', openInNewTab: navigationStore.isHomePage, keywords: ['meals'] });
        (hasAccessToDutyRelatedReport || hasAccessToGroundStaffStatisticReport) && reports.push({ type: 'link', toUrl: '/reports/ground-staff', title: 'GroundStaff Statistics Report', openInNewTab: navigationStore.isHomePage });
        hasAccessToDosimetryReport && reports.push({ type: 'link', toUrl: '/reports/dosimetry', title: 'Dosimetry Report', openInNewTab: navigationStore.isHomePage });
        hasAccessToSeniorityReport && this._enableExperienceReport && reports.push({ type: 'link', toUrl: '/reports/seniority', title: 'Seniority Report', openInNewTab: navigationStore.isHomePage });
        hasAccessToOutageReport && appStore.isCtrlKey && reports.push({ type: 'action', title: 'Outage Report', onClick: () => this._downloadLink('/api/tempfile/outage-roster-report') });
        this._featureEnableCabinAttestation && hasAccessToCourseManagement && reports.push({ type: 'link', toUrl: '/reports/cca-report', title: 'Cabin Crew Attestation Report', openInNewTab: navigationStore.isHomePage });
        this._featureEnableSimCourseOverview && hasAccessToDutyRelatedReport && reports.push({ type: 'link', toUrl: '/reports/simulator', title: 'Simulator Report', openInNewTab: navigationStore.isHomePage });
        this._hasReachGavCockpit2024JulyEffectiveDate && hasAccessToDutyRelatedReport && reports.push({ type: 'link', toUrl: '/reports/crew-notification-time', title: 'Crew Notification Time Report', openInNewTab: navigationStore.isHomePage });

        return reports;
    }

    private get _viewItems() {
        return this._getDynamicItems(MenuSection.View);
    }

    render() {
        const className = appStore.isPrintMode ? 'main-nav-hidden' : 'main-nav';
        const hoverMenuItemDelay = this._isAnyMenuItemVisible ? undefined : 0;
        const hasAccessToGenDec = AzureAuth.hasAccess(UserPermissions.GenDecRead);

        return (
            <div className={className}>
                <div className="main-nav-menu">
                    <div className="main-nav-menu-left">
                        <div className="main-nav-brand" style={{ backgroundImage: 'url("images/edelweiss-logo-30y.svg")' }}>
                            {this.renderNavMenuItem({ type: 'link', toUrl: '/', title: '' })}
                        </div>
                        <div className="main-nav-group">
                            <span className="main-nav-group-name">
                                {this.renderNavMenuItem({ type: 'link', toUrl: '/', title: 'Home' })}
                            </span>
                        </div>
                        {!!this._tools.length && (
                            <HoverContainer
                                className="main-nav-group"
                                hoverClassname="menu-hover"
                                title={<span className="main-nav-group-name">Tools</span>}
                                delay={hoverMenuItemDelay}
                                onShow={this._onShowMenuItem}
                                onHide={this._onHideMenuItem}
                            >
                                <div className="main-nav-links">
                                    {this._tools.map(item => this.renderNavMenuItem(item))}
                                </div>
                            </HoverContainer>
                        )}
                        {!!this._reports.length && (
                            <HoverContainer
                                className="main-nav-group"
                                hoverClassname="menu-hover"
                                title={<span id="main-nav-group-reports" className="main-nav-group-name">Reports</span>}
                                delay={hoverMenuItemDelay}
                                onShow={this._onShowMenuItem}
                                onHide={this._onHideMenuItem}
                            >
                                <div className="main-nav-links">
                                    {this._reports.map(item => this.renderNavMenuItem(item))}
                                </div>
                            </HoverContainer>
                        )}
                        {hasAccessToGenDec && (
                            <div className="main-nav-group">
                                <span className="main-nav-group-name">
                                    {this.renderNavMenuItem({ type: 'link', toUrl: '/gen-dec', title: 'GenDec' })}
                                </span>
                            </div>
                        )}
                        {!!this._viewItems.length && (
                            <HoverContainer
                                className="main-nav-group"
                                hoverClassname="menu-hover"
                                title={<span className="main-nav-group-name">VIEW</span>}
                                delay={hoverMenuItemDelay}
                                onShow={this._onShowMenuItem}
                                onHide={this._onHideMenuItem}
                            >
                                <div className="main-nav-links check-links">
                                    {this._viewItems.map(item => this.renderNavMenuItem(item))}
                                </div>
                            </HoverContainer>
                        )}
                    </div>
                    <div className="main-nav-menu-left">
                        {this._showSearch && (
                            <div className="main-nav-search">
                                <Input
                                    type="search"
                                    placeholder="🔎︎ Find report/tool"
                                    className="main-nav-search-field"
                                    value={this._searchValue}
                                    onChange={(e) => this._onSearch(e.target.value)}
                                />
                                {this._searchValue && (
                                    <div className="main-nav-links check-links customScrollbar">
                                        {this._searchOptions.map(item => this.renderNavMenuItem(item, true))}
                                        {!this._searchOptions.length && <div className="no-data text-muted">No data</div>}
                                    </div>
                                )}
                            </div>
                        )}

                        <div className={'main-nav-application ' + devInfoStore.envClassName}>
                            {navMenuStore.menuTitle ? <span className="app-function">{navMenuStore.menuTitle}</span> : null}
                            <span className="main-nav-version">
                                CrewPage:<span className="app-name">{devInfoStore.appEnvironment}</span>
                                <span className="app-version">- Version:<span className="app-version-number">{devInfoStore.appVersion}</span></span>
                                <span className="fa fa-info-circle app-version-info-icon" title={`Version: ${devInfoStore.appVersion}`} />
                                {navMenuStore.hasRenderBlocker && <span className="fa fa-exclamation-triangle" title="ADs blocker detected, please turn it off for CrewPage to improve site performance!" />}
                            </span>
                        </div>
                        <ThemeSwitcher />
                    </div>
                    {this._helpLink && (
                        <div className="main-nav-menu-right">
                            <HelpLink />
                        </div>
                    )}
                    <div className="main-nav-menu-right">
                        <LoginSection />
                    </div>
                    {this._renderDynamicExtensions()}
                </div>
            </div>
        );
    }

    renderNavMenuItem(item: MenuItem, highlight?: boolean) {
        if (item.type === "separator") {
            return <div key={item.id} className="nav-link-separator" />;
        }

        if (item.type === "link") {
            const currentLocation = navigationStore.currentLocation.pathname.split('?')[0];
            const currentLocationParts = currentLocation.split('/').filter(x => !!x);
            const toUrlParts = item.toUrl.toString().split('/').filter(x => !!x);
            const isActive = currentLocationParts.length && currentLocationParts.every(x => toUrlParts.includes(x));

            return (
                <Link key={item.title} to={item.toUrl} target={item.openInNewTab ? '_blank' : '_self'} className={'nav-link' + (isActive ? ' active' : '')} onClick={this._clearSearch}>
                    {highlight ? this._renderText(item.title) : item.title}
                    {highlight && (
                        <div className="nav-link-keywords">
                            {item.keywords?.map((keyword, i) => {
                                const parts = stringSearchService.markMatches(keyword, this.searchTerms, this.textComparer);
                                const hasMatches = parts.some(p => !!p.isMatch);
                                if(!hasMatches) return null;

                                return (
                                    <React.Fragment key={`${keyword}_${i}`}>{this._renderText(keyword)}</React.Fragment>
                                );
                            })}
                        </div>
                    )}
                </Link>
            );
        }

        if (item.type === "action") {
            return (
                <span key={item.title} className={item.className || 'nav-link'} onClick={item.onClick}>
                    {highlight ? this._renderText(item.title) : item.title}
                </span>
            );
        }

        return null;
    }

    private _renderDynamicExtensions() {
        return navMenuStore.dynamicExtensionItems.map((item) => {
            if (item.isHidden) return <React.Fragment key={item.id} />;
            return (
                <div key={item.id} className={`main-nav-menu-right ${item.className}`}>
                    {item.itemRender()}
                </div>
            );
        });
    }

    private _renderText(text: string | undefined) {
        const parts = stringSearchService.markMatches(text, this.searchTerms, this.textComparer);
        return (
            <div>
                {parts.map((p, index) =>
                    p.isMatch ? (
                        <span className="text-match" key={'t' + index}>
                            {`${p.text}`}
                        </span>
                    ) : p.text
                )}
            </div>
        );
    }

    private _getDynamicItems(section: MenuSection) {
        const navItems: MenuItem[] = [];
        const dynamicItems = navMenuStore.dynamicMenuItems
            .filter(i => i.section === section)
            .sort((a, b) => a.order - b.order)
            .filter(i => {
                if (typeof i.isHidden === 'boolean') return !i.isHidden;
                if (typeof i.isHidden === 'function') return !i.isHidden();
                return true;
            });

        dynamicItems.forEach(i => {
            if (i.title === '-') {
                navItems.push({ type: 'separator', title: i.title, id: i.id });
            } else {
                navItems.push({
                    type: 'action',
                    title: i.title,
                    onClick: i.onClick ? i.onClick : () => void 0,
                    className: this._viewClass(i.onGetState && i.onGetState())
                });
            }
        });

        return navItems;
    }

    private _downloadLink(toUrl: string) {
        const encodedToken = getEncodedToken(appStore.currentToken);
        window.location.href = toUrl + '?token=' + encodeURIComponent(encodedToken);
    }

    private _viewClass(isActive?: boolean) {
        return 'nav-link' + (isActive ? ' active-view' : '');
    }

    @action.bound
    private _onShowMenuItem() {
        this._isAnyMenuItemVisible = true;
    }

    @action.bound
    private _onHideMenuItem() {
        if (this._isAnyMenuItemVisible) {
            this._isAnyMenuItemVisible = false;
        }
    }

    @action
    private _onSearch(value: string) {
        this._searchValue = value;
    }

    @action.bound
    private _clearSearch() {
        this._searchValue = '';
    }

    @computed
    get textComparer() {
        const comparator: MatchComparer = (v, t, s) => {
            return stringSearchService.prepareString(v)?.indexOf(stringSearchService.prepareString(t) ?? '', s) ?? -1;
        };
        return comparator;
    }

    @computed
    get searchTerms() {
        const searchStr = this._searchValue;
        return searchStr.split(/[ ,]+/).filter((x) => !!x);
    }

    private get _searchOptions() {
        let filteredList = [...this._tools, ...this._reports, ...this._viewItems];
        const searchStringParts = this.searchTerms;

        if (searchStringParts.length) {
            const comparator = this.textComparer;

            filteredList = filteredList.filter((item: MenuItem): boolean => {
                let anyMatch = true;

                for (const term of searchStringParts) {
                    if (comparator(item.title, term, 0) === -1) {
                        anyMatch = false;
                    }

                    if ('keywords' in item) {
                        for (const keyword of item.keywords!) {
                            if (comparator(keyword, term, 0) !== -1) {
                                anyMatch = true;
                            }
                        }
                    }
                }

                if (!anyMatch) {
                    return false;
                }

                return true;
            });
        }

        return [...filteredList];
    }

    private get _showSearch(){
        const allNavItems = [...this._tools, ...this._reports, ...this._viewItems];
        return allNavItems.length > 5;
    }
}

class NavMenuStore {
    @observable public hasRenderBlocker: boolean = false;
    @observable public dynamicMenuItems: DynamicMenuItem[] = [];
    @observable public dynamicExtensionItems: ExtensionItem[] = [];
    @observable menuTitle: string | null = null;

    constructor() {
        applyDecorators(this);
    }

    public registerMenuItem(item: DynamicMenuItem, ignoreSecondRegistration?: boolean) {
        if (this.dynamicMenuItems.find((i) => i.id === item.id)) {
            if (ignoreSecondRegistration) return;
            throw new Error(`Menu item with id = ${item.id} is already registered!`);
        }
        this.dynamicMenuItems.push(item);
    }

    public unregisterMenuItem(itemId: string) {
        const itemIndex = this.dynamicMenuItems.findIndex((i) => i.id === itemId);
        if (itemIndex === -1) {
            throw new Error(`Menu item with id = ${itemId} is not registered!`);
        }
        this.dynamicMenuItems.splice(itemIndex, 1);
    }

    public registerExtensionItem(item: ExtensionItem, ignoreSecondRegistration?: boolean) {
        if (this.dynamicExtensionItems.find((i) => i.id === item.id)) {
            if (ignoreSecondRegistration) return;
            throw new Error(`Extension item with id = ${item.id} is already registered!`);
        }
        this.dynamicExtensionItems.push(item);
    }

    public unregisteExtensionItem(itemId: string) {
        const itemIndex = this.dynamicExtensionItems.findIndex((i) => i.id === itemId);
        if (itemIndex === -1) {
            throw new Error(`Extension item with id = ${itemId} is not registered!`);
        }
        this.dynamicExtensionItems.splice(itemIndex, 1);
    }
}

export const navMenuStore = new NavMenuStore();
